import React from "react";

// Mar 2026 · Universal document-status palette. Add-on to the machine
// state palette below so every document dashboard (Invoices, SO, ESO,
// PO, WO, ECI …) can use one shared component with the exact colours
// operators asked for:
//   Grey        → Draft (new / unsaved / unsubmitted)
//   Orange      → Pending Authorisation (awaiting manager sign-off)
//   Green       → Authorised (officially approved)
//   Red         → Rejected (sent back by management)
//   Dark Grey   → Cancelled / Short Closed (voided)
const DOC_STATUSES = {
  Draft:              { bg: "bg-zinc-500/15",   text: "text-zinc-400",    dot: "bg-zinc-400" },
  PendingApproval:    { bg: "bg-orange-500/15", text: "text-orange-400",  dot: "bg-orange-500" },
  "Pending Approval": { bg: "bg-orange-500/15", text: "text-orange-400",  dot: "bg-orange-500" },
  Pending:            { bg: "bg-orange-500/15", text: "text-orange-400",  dot: "bg-orange-500" },
  Authorised:         { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-500" },
  Authorized:         { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-500" },
  Approved:           { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-500" },
  Rejected:           { bg: "bg-red-500/15",     text: "text-red-400",     dot: "bg-red-500" },
  Cancelled:          { bg: "bg-zinc-700/40",    text: "text-zinc-500",    dot: "bg-zinc-600" },
  "Short Closed":     { bg: "bg-zinc-700/40",    text: "text-zinc-500",    dot: "bg-zinc-600" },
};

const COLORS = {
  ...DOC_STATUSES,
  Running: { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-500", ring: "ring-emerald-500/30" },
  Idle: { bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-500", ring: "ring-amber-500/30" },
  Setup: { bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-500", ring: "ring-amber-500/30" },
  Breakdown: { bg: "bg-red-500/15", text: "text-red-400", dot: "bg-red-500", ring: "ring-red-500/30" },
  Occupied: { bg: "bg-orange-500/15", text: "text-orange-400", dot: "bg-orange-500", ring: "ring-orange-500/30" },
  Maintenance: { bg: "bg-blue-500/15", text: "text-blue-400", dot: "bg-blue-500", ring: "ring-blue-500/30" },
  "Power Off": { bg: "bg-zinc-500/15", text: "text-zinc-400", dot: "bg-zinc-500", ring: "ring-zinc-500/30" },
  Open: { bg: "bg-zinc-500/15", text: "text-zinc-300", dot: "bg-zinc-400" },
  Planned: { bg: "bg-blue-500/15", text: "text-blue-400", dot: "bg-blue-500" },
  Completed: { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-500" },
  Closed: { bg: "bg-zinc-500/15", text: "text-zinc-400", dot: "bg-zinc-500" },
  Pass: { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-500" },
  Hold: { bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-500" },
  Reject: { bg: "bg-red-500/15", text: "text-red-400", dot: "bg-red-500" },
  Awaiting: { bg: "bg-amber-500/15", text: "text-amber-300", dot: "bg-amber-400" },
  Critical: { bg: "bg-red-500/15", text: "text-red-400", dot: "bg-red-500" },
  High: { bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-500" },
  Normal: { bg: "bg-blue-500/15", text: "text-blue-400", dot: "bg-blue-500" },
  Low: { bg: "bg-zinc-500/15", text: "text-zinc-400", dot: "bg-zinc-500" },
};

// Mar 2026 · Normalise raw auth_status / status values (camelCase,
// mixed spelling, empty strings) into the canonical badge label so
// callers can pass the raw DB field without pre-massaging it.
export function normaliseDocStatus(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "Draft";
  const m = s.toLowerCase();
  if (m === "pendingapproval" || m === "pending_approval") return "Pending Approval";
  if (m === "authorized") return "Authorised";
  if (m === "approved")   return "Authorised";
  return s;
}

export default function StatusBadge({ status, pulse = false, testId }) {
  const label = normaliseDocStatus(status);
  const c = COLORS[label] || COLORS[status] || COLORS.Idle;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium ${c.bg} ${c.text} border border-white/5`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} ${pulse ? "status-pulse" : ""}`} />
      {label}
    </span>
  );
}
